import type { ApolloCache } from "@apollo/client"
import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Text,
  VStack
} from "@chakra-ui/react"
import type { IconName } from "@pathwright/pathicons"
import { flattenEdges } from "@pathwright/utils"
import PanelSection from "@pathwright/ux/src/components/augmentation/AugmentationPanelSection"
import { AugmentationComponent } from "@pathwright/ux/src/components/augmentation/AugmentationToolbar"
import { useOpenAugmentationSubpanel } from "@pathwright/ux/src/components/augmentation/useAugmentationSubpanel"
import {
  AUGMENTATION_PANEL_INPUT_PROPS,
  AUGMENTATION_PANEL_SELECT_PROPS,
  AUGMENTATION_PANEL_STATIC_TEXT_PROPS
} from "@pathwright/ux/src/components/augmentation/augmentationPanelStyles"
import { toaster } from "@pathwright/ux/src/components/chakra/ui/toaster"
import CustomSelect from "@pathwright/ux/src/components/form/CustomSelect"
import Field from "@pathwright/ux/src/components/form/Field"
import { FormikWithDiscardFormState } from "@pathwright/ux/src/components/form/state/FormikDiscardFormState"
import KeyboardFormSubmit from "@pathwright/ux/src/components/form/state/KeyboardFormSubmit"
import Link, { LinkProps } from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import { Form, FormikProps, useFormikContext } from "formik"
import { useMemo } from "react"
import { useHistory } from "react-router-dom"
import { z } from "zod"
import { toFormikValidate } from "zod-formik-adapter"
import {
  CoverPageOfferFragment as CoverPageOffer,
  CoverPageOfferableCohortsDocument,
  CoverPageOfferableCohortsQuery,
  CoverPageOfferableCohortsQueryVariables,
  CreateGroupMutation,
  GroupFragment,
  OfferKind,
  StoreOfferInput,
  useCoverPageOfferableCohortsQuery,
  useCreateOfferMutation,
  useGroupQuery,
  useStoreQuery,
  useUpdateOfferMutation
} from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { useCoverPageContext } from "../CoverPageProvider"
import { useCoverPageIsEditable } from "../access"
import { COVER_PAGE_PANEL_ROOT_KEY } from "../constants"
import type { CoverPageOfferableCohort } from "../types"
import { CoverPageHashSection } from "../types"
import { useCoverPageTranslate } from "../utils"
import { CoverPagePanelFormActions } from "./CoverPagePanelFormActions"
import CoverPagePanelOfferCreateCohortGate from "./CoverPagePanelOfferCreateCohortGate"
import { CoverPagePanelRichTextField } from "./CoverPagePanelRichTextField"
import {
  formatCoverPageScrollHash,
  useCoverPagePanelErrorMessage
} from "./utils"

export type CoverPageT = ReturnType<typeof useCoverPageTranslate>["t"]

const offerSchema = z.object({
  kind: z.nativeEnum(OfferKind),
  targetCohortId: z.string(),
  title: z.string(),
  highlights: z.string(),
  buttonLabel: z.string().max(25, "Button label must be 25 characters or less"),
  accessLabel: z.string(),
  pricingLabel: z.string(),
  externalUrl: z.string()
})

export type CoverPagePanelOfferValues = z.infer<typeof offerSchema>

// Picker value that opens the New Cohort panel instead of selecting a Cohort.
const CREATE_COHORT_OPTION_VALUE = "create_cohort"
export const NEW_COHORT_VISIBILITY = 5

export const COVER_PAGE_NEW_OFFER_PANEL_PATH = `/${COVER_PAGE_PANEL_ROOT_KEY}/offers/new`
export const COVER_PAGE_NEW_OFFER_COHORT_PANEL_PATH = `${COVER_PAGE_NEW_OFFER_PANEL_PATH}/cohorts/new`

// The New offer draft outlives the panel (e.g. while its New Cohort panel is
// open), so it's keyed by Path rather than shared across Cover Pages.
export function getNewOfferFormStateKey(resourceId: number) {
  return ["cover-page", resourceId, "offer", "new"]
}

export function createOfferValidator(t: CoverPageT) {
  return toFormikValidate(
    offerSchema.superRefine((values, ctx) => {
      if (!values.title.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["title"],
          message: t("panel_offer.title_required", {
            defaultValue: "Enter an offer title."
          })
        })
      }

      if (
        values.kind === OfferKind.JoinCohort &&
        !Number(values.targetCohortId)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["targetCohortId"],
          message: t("panel_offer.select_a_cohort", {
            defaultValue: "Select a Cohort."
          })
        })
      }

      if (values.kind === OfferKind.External && !values.externalUrl.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["externalUrl"],
          message: t("panel_offer.enter_a_link", {
            defaultValue: "Enter a link."
          })
        })
      }
    })
  )
}

export function addCohortToOfferableCohortsCache(
  cache: ApolloCache<unknown>,
  resourceId: number,
  cohort: NonNullable<CreateGroupMutation["createGroup"]>
) {
  cache.updateQuery<
    CoverPageOfferableCohortsQuery,
    CoverPageOfferableCohortsQueryVariables
  >(
    {
      query: CoverPageOfferableCohortsDocument,
      variables: { resourceId }
    },
    (offerableCohortsData) => {
      if (!offerableCohortsData?.groups) return offerableCohortsData

      if (
        offerableCohortsData.groups.edges.some(
          (edge) => edge.node.id === cohort.id
        )
      ) {
        return offerableCohortsData
      }

      return {
        ...offerableCohortsData,
        groups: {
          ...offerableCohortsData.groups,
          edges: [
            {
              __typename: "GroupEdge" as const,
              node: {
                __typename: "Group" as const,
                id: cohort.id,
                name: cohort.name,
                visibility: cohort.visibility,
                registration_starts: cohort.registration_starts,
                registration_ends: cohort.registration_ends,
                access_starts: cohort.access_starts,
                access_ends: cohort.access_ends,
                price: cohort.price,
                is_subscription_only: cohort.is_subscription_only
              }
            },
            ...offerableCohortsData.groups.edges
          ]
        }
      }
    }
  )
}

function navigateToCoverPageOffersSection(
  scrollToSection: (section?: string | null) => void
) {
  const hash = formatCoverPageScrollHash(CoverPageHashSection.Offers)

  scrollToSection(hash)

  if (typeof window === "undefined") return

  const syncHash = () => {
    const url = new URL(window.location.href)
    url.hash = hash
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`
    )
    scrollToSection(hash)
  }

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(syncHash)
  })
}

export const getOfferKindOptions = (
  t: CoverPageT,
  {
    hasStore,
    canAdministrate,
    spaceName
  }: { hasStore: boolean; canAdministrate: boolean; spaceName?: string }
) =>
  [
    {
      value: OfferKind.JoinCohort,
      label: t("panel_offer.join_a_cohort", { defaultValue: "Join a Cohort" }),
      description: spaceName
        ? t("panel_offer.join_a_cohort_description", {
            defaultValue: "Join a Cohort managed by {{spaceName}}",
            spaceName
          })
        : t("panel_offer.join_a_cohort_description_no_space", {
            defaultValue: "Join a Cohort managed by this Learning Space"
          }),
      icon: "avatar-plus-circle"
    },
    {
      value: OfferKind.CreateCohort,
      label: t("panel_offer.create_a_private_cohort", {
        defaultValue: "Create a Private Cohort"
      }),
      description: t("panel_offer.create_a_private_cohort_description", {
        defaultValue:
          "A custom Cohort they manage and take with their own group"
      }),
      icon: "group"
    },
    // Same gate as the offer connection query: license_to_space is visible
    // only to school admins and superusers (can_administrate).
    ...(hasStore && canAdministrate
      ? [
          {
            value: OfferKind.LicenseToSpace,
            label: t("panel_offer.license_in_your_own_learning_space", {
              defaultValue: "License in your own Learning Space"
            }),
            description: t(
              "panel_offer.license_in_your_own_learning_space_description",
              {
                defaultValue:
                  "Another Learning Space teaches this Path to its own members"
              }
            ),
            icon: "group-plus" as IconName
          }
        ]
      : [])
    // TODO: add external link Offers once we can support them
    // {
    //   value: OfferKind.External,
    //   label: t("panel_offer.external_link", { defaultValue: "External Link" }),
    //   icon: "external-link"
    // }
  ] as Array<{
    value: OfferKind
    label: string
    description: string
    icon: IconName
  }>

function useCoverPageOffer(offerId?: string) {
  const offers = useCoverPageContext((value) => value.offers)

  return useMemo(
    () => offers?.find((offer) => offer.id === Number(offerId)) || null,
    [offerId, offers]
  )
}

function getCurrentOfferKind({
  offer,
  formKind
}: {
  offer: CoverPageOffer | null
  formKind: OfferKind
}): OfferKind {
  return offer?.kind || formKind
}

function getDefaultButtonLabel(kind: OfferKind, t: CoverPageT) {
  if (kind === OfferKind.CreateCohort)
    return t("panel_offer.start_your_cohort", {
      defaultValue: "Start Your Cohort"
    })
  if (kind === OfferKind.LicenseToSpace)
    return t("panel_offer.get_path", { defaultValue: "Get Path" })
  if (kind === OfferKind.External)
    return t("panel_offer.apply_now", { defaultValue: "Apply Now" })

  return t("join", { defaultValue: "Join" })
}

// Switching offer type swaps in the new type's default label, unless the
// label was customized.
export function getButtonLabelForKindChange(
  {
    buttonLabel,
    fromKind,
    toKind
  }: { buttonLabel: string; fromKind: OfferKind; toKind: OfferKind },
  t: CoverPageT
) {
  const isCustomized =
    buttonLabel.trim() !== "" &&
    buttonLabel !== getDefaultButtonLabel(fromKind, t)

  return isCustomized ? buttonLabel : getDefaultButtonLabel(toKind, t)
}

function getPrimaryPriceOption(offer: CoverPageOffer) {
  return offer.price_options[0]
}

function getOfferCohortId(offer: CoverPageOffer | null) {
  if (!offer) return null

  if (offer.kind === OfferKind.JoinCohort) return offer.cohort_id

  return null
}

function getOfferExternalUrl(offer: CoverPageOffer | null) {
  return (offer?.external_url || "").trim()
}

function getVisibilityLabel(
  source: { visibility?: number | null },
  t: CoverPageT
) {
  if (source.visibility === null || source.visibility === undefined) return null

  if (source.visibility >= 15)
    return t("panel_offer.public", { defaultValue: "Public" })
  if (source.visibility > 5)
    return t("panel_offer.members", { defaultValue: "Members" })

  return t("panel_offer.invite_only", { defaultValue: "Invite only" })
}

function getIconForVisibility(visibility?: number | null): IconName {
  if (visibility === null || visibility === undefined) return "lock"
  if (visibility >= 15) return "globe"
  if (visibility > 5) return "group"

  return "avatar-lock"
}

function getIconForCohort(cohort: CoverPageOfferableCohort): IconName {
  return getIconForVisibility(cohort.visibility)
}

function formatOfferDate(date: string | null | undefined) {
  if (!date) return null

  return new Intl.DateTimeFormat(undefined, {
    month: "numeric",
    day: "numeric",
    year: "numeric"
  }).format(new Date(date))
}

function getAvailabilityLabel({
  startsAt,
  endsAt,
  t
}: {
  startsAt?: string | null
  endsAt?: string | null
  t: CoverPageT
}) {
  const starts = formatOfferDate(startsAt)
  const ends = formatOfferDate(endsAt)

  if (starts && ends) return `${starts} - ${ends}`
  if (starts)
    return t("panel_offer.from_date", {
      defaultValue: "From {{date}}",
      date: starts
    })
  if (ends)
    return t("panel_offer.until_date", {
      defaultValue: "Until {{date}}",
      date: ends
    })

  return null
}

function getOfferAvailabilityLabels(offer: CoverPageOffer, t: CoverPageT) {
  const availability = getAvailabilityLabel({
    startsAt: offer.availability_starts,
    endsAt: offer.availability_ends,
    t
  })

  if (!availability)
    return [t("panel_offer.anytime", { defaultValue: "Anytime" })]

  return [availability]
}

function getOfferAccessLabel(offer: CoverPageOffer, t: CoverPageT) {
  const label = [
    getVisibilityLabel(offer, t),
    ...getOfferAvailabilityLabels(offer, t)
  ]
    .filter(Boolean)
    .join(", ")

  return label || null
}

function getCohortAccessLabel(cohort: GroupFragment, t: CoverPageT) {
  const visibility = getVisibilityLabel(cohort, t)
  const availability =
    getAvailabilityLabel({
      startsAt: cohort.registration_starts || cohort.access_starts,
      endsAt: cohort.registration_ends || cohort.access_ends,
      t
    }) ||
    (visibility ? t("panel_offer.anytime", { defaultValue: "Anytime" }) : null)
  const label = [visibility, availability].filter(Boolean).join(", ")

  return label || null
}

function stripHtml(html: string) {
  return html.replace(/<[^>]*>/g, "").trim()
}

function getOfferPricingLabel(offer: CoverPageOffer) {
  const priceOption = getPrimaryPriceOption(offer)

  return (
    stripHtml(
      priceOption?.price_description || priceOption?.option_label || ""
    ) || null
  )
}

function getCohortPricingLabel(cohort: GroupFragment, t: CoverPageT) {
  if (cohort.is_subscription_only)
    return t("panel_offer.subscription", { defaultValue: "Subscription" })
  if (typeof cohort.price === "number" && cohort.price > 0) {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: cohort.price % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(cohort.price)
  }
  if (cohort.price === 0 || cohort.is_subscription_only === false) {
    return t("panel_offer.free", { defaultValue: "Free" })
  }

  return null
}

export function buildOfferInitialValues(
  offer: CoverPageOffer | null,
  t: CoverPageT
): CoverPagePanelOfferValues {
  const kind = offer?.kind || OfferKind.JoinCohort
  let targetCohortId = ""

  if (offer && kind === OfferKind.JoinCohort) {
    targetCohortId = String(getOfferCohortId(offer) || "")
  }

  return {
    kind,
    targetCohortId,
    title: offer?.title || "",
    highlights: offer?.description || "",
    buttonLabel: offer ? offer.cta_label || "" : getDefaultButtonLabel(kind, t),
    accessLabel: offer ? getOfferAccessLabel(offer, t) || "" : "",
    pricingLabel: offer ? getOfferPricingLabel(offer) || "" : "",
    externalUrl: getOfferExternalUrl(offer)
  }
}

function OfferKindControl({
  disabled,
  kind
}: {
  disabled: boolean
  kind: OfferKind
}) {
  const { t } = useCoverPageTranslate()
  const form = useFormikContext<CoverPagePanelOfferValues>()
  const { me, school } = usePathwrightContext()
  const storeQuery = useStoreQuery()
  const hasStore =
    !storeQuery.loading && !storeQuery.error && Boolean(storeQuery.data?.store)
  const canAdministrate = Boolean(me?.permissions?.can_administrate)
  const spaceName = school?.name || undefined
  const offerKindOptions = useMemo(
    () => getOfferKindOptions(t, { hasStore, canAdministrate, spaceName }),
    [canAdministrate, hasStore, spaceName, t]
  )

  return (
    <HStack align="center" gap={2} wrap="wrap">
      <Text {...AUGMENTATION_PANEL_STATIC_TEXT_PROPS}>
        {t("panel_offer.offer_type_label", {
          defaultValue: "Offer type:"
        })}
      </Text>

      <CustomSelect
        value={kind}
        disabled={disabled}
        minW="150px"
        triggerProps={{
          ...AUGMENTATION_PANEL_SELECT_PROPS,
          "aria-label": t("panel_offer.offer_kind", {
            defaultValue: "Offer kind"
          })
        }}
        contentProps={{
          borderRadius: "augmentationPanel",
          minW: "max-content",
          // Descriptions wrap rather than stretching the menu across the panel.
          maxW: "min(280px, calc(100vw - 32px))"
        }}
        options={offerKindOptions.map((option) => ({
          value: option.value,
          label: option.label,
          description: option.description
        }))}
        onChange={(value) => {
          const kind = value as OfferKind
          void form.setFieldValue(
            "buttonLabel",
            getButtonLabelForKindChange(
              {
                buttonLabel: form.values.buttonLabel,
                fromKind: form.values.kind,
                toKind: kind
              },
              t
            )
          )
          void form.setFieldValue("kind", kind)
        }}
      />
      <Tooltip
        trigger="click"
        content={{
          w: "320px",
          maxW: "calc(100vw - 32px)",
          maxH: "calc(100dvh - 48px)",
          overflowY: "auto",
          p: 4
        }}
        title={
          <VStack align="stretch" gap={2}>
            <Text m={0} color="inherit">
              {kind === OfferKind.JoinCohort
                ? t("panel_offer.join_selected_type_help", {
                    defaultValue:
                      "Learners join a Cohort you run, with the schedule, teachers, and pricing you set."
                  })
                : kind === OfferKind.CreateCohort
                ? t("panel_offer.private_selected_type_help", {
                    defaultValue:
                      "The creator invites their own members and guides them through this Path, with the access and pricing you set."
                  })
                : t("panel_offer.license_selected_type_help", {
                    defaultValue:
                      "An administrator of another Learning Space adds this Path from your Store and runs it for their own members."
                  })}
            </Text>
            {kind === OfferKind.JoinCohort ||
            kind === OfferKind.CreateCohort ? (
              <Link
                href={
                  kind === OfferKind.JoinCohort
                    ? "https://help.pathwright.com/en/articles/459249-cohorts-help-everyone-learn-together-again"
                    : "https://help.pathwright.com/en/articles/430587-member-created-cohorts-allow-anyone-to-lead-a-private-group"
                }
                target="_blank"
                rel="noopener noreferrer"
                color="inherit"
                textDecoration="underline"
              >
                {kind === OfferKind.JoinCohort
                  ? t("panel_offer.join_learn_more", {
                      defaultValue: "Learn more about Cohorts"
                    })
                  : t("panel_offer.private_learn_more", {
                      defaultValue: "Learn more about Private Cohorts"
                    })}
              </Link>
            ) : null}
          </VStack>
        }
      >
        <IconButton
          aria-label={t("panel_offer.offer_types_help", {
            defaultValue: "About offer types"
          })}
          variant="ghost"
          size="xs"
          minW="6"
          w="6"
          h="6"
          color="fg.muted"
          type="button"
        >
          <Pathicon icon="info-circle" />
        </IconButton>
      </Tooltip>
    </HStack>
  )
}

function OfferStaticPill({ value }: { value: string }) {
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      minH="32px"
      maxW="100%"
      px={3}
      borderRadius="full"
      bg="white"
      color="fg"
      fontSize="sm"
      fontWeight="bold"
      boxShadow="sm"
    >
      <Text as="span" m={0} truncate>
        {value}
      </Text>
    </Box>
  )
}

function ExistingOfferAccessSummary({
  offer,
  kind
}: {
  offer: CoverPageOffer
  kind: OfferKind
}) {
  const { t } = useCoverPageTranslate()
  const resource = useCoverPageContext((value) => value.resource)
  const cohortPath =
    kind === OfferKind.JoinCohort && resource?.slug && offer.cohort_id
      ? `/library/${resource.slug}/${offer.cohort_id}/path/`
      : null
  const label =
    kind === OfferKind.CreateCohort
      ? t("panel_offer.create_private_cohort_from", {
          defaultValue: "Create a private Cohort from"
        })
      : kind === OfferKind.LicenseToSpace
      ? t("panel_offer.licenses_path_to", {
          defaultValue: "Licenses Path to"
        })
      : kind === OfferKind.External
      ? t("panel_offer.links_to", { defaultValue: "Links to" })
      : t("panel_offer.grants_access_to", {
          defaultValue: "Grants access to"
        })
  const value =
    kind === OfferKind.CreateCohort
      ? t("path_preview.source_path", { defaultValue: "Source Path" })
      : kind === OfferKind.LicenseToSpace
      ? t("panel_offer.another_learning_space", {
          defaultValue: "another Learning Space"
        })
      : kind === OfferKind.External
      ? offer.external_url ||
        t("panel_offer.external_resource", {
          defaultValue: "External resource"
        })
      : offer.cohort_name || t("panel_offer.cohort", { defaultValue: "Cohort" })

  return (
    <HStack align="center" gap={2} wrap="wrap" minW={0}>
      <Text {...AUGMENTATION_PANEL_STATIC_TEXT_PROPS}>{label}</Text>
      {cohortPath ? (
        <Link
          href={cohortPath}
          variant="plain"
          cursor="pointer"
          minW={0}
          maxW="100%"
          textDecoration="none"
          _hover={{ textDecoration: "none" }}
        >
          <OfferStaticPill value={value} />
        </Link>
      ) : (
        <OfferStaticPill value={value} />
      )}
    </HStack>
  )
}

/**
 * A pill that leaves the panel for somewhere else in the app. It opens a new
 * tab: the offer being described here is half-filled in, and navigating away
 * from an unsaved form to look at what it points to would discard it.
 */
function OfferPillLink({
  icon,
  value,
  to,
  disabled = false
}: {
  icon: IconName
  value: string
  to?: string
  disabled?: boolean
}) {
  const content = (
    <>
      <Pathicon icon={icon} />
      {value}
    </>
  )

  if (!to || disabled) {
    return (
      <Button
        type="button"
        size="xs"
        borderRadius="full"
        variant="surface"
        alignSelf="flex-start"
        disabled
      >
        {content}
      </Button>
    )
  }

  return (
    <Button
      asChild
      type="button"
      size="xs"
      borderRadius="full"
      variant="surface"
      alignSelf="flex-start"
    >
      <Link
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        textDecoration="none"
      >
        {content}
      </Link>
    </Button>
  )
}

function OfferReadonlySetting({
  icon,
  label,
  value,
  ...linkProps
}: {
  icon: IconName
  label: string
  value: string
} & LinkProps) {
  return (
    <HStack align="center" gap={2} minW={0}>
      <Text as="span" {...AUGMENTATION_PANEL_STATIC_TEXT_PROPS}>
        {label}:
      </Text>
      <Link {...linkProps}>
        <HStack
          as="span"
          gap={1}
          minW={0}
          textStyle="augmentationPanel"
          textDecoration="none"
        >
          <Pathicon icon={icon} />
          <Text as="span" m={0} truncate>
            {value}
          </Text>
        </HStack>
      </Link>
    </HStack>
  )
}

function OfferHighlightsField({
  disabled,
  kind
}: {
  disabled: boolean
  kind: OfferKind
}) {
  const { t } = useCoverPageTranslate()
  // The editor's placeholder keeps line breaks, so these read as a bulleted
  // list without seeding the field with text to delete.
  const placeholder =
    kind === OfferKind.CreateCohort
      ? t("panel_offer.private_cohort_highlights_examples", {
          defaultValue:
            "•  Take it privately with your own members\n•  Guide everyone and give feedback\n•  Track everyone's progress"
        })
      : kind === OfferKind.JoinCohort
      ? t("panel_offer.join_cohort_highlights_examples", {
          defaultValue:
            "•  Live sessions with your teacher\n•  A set schedule with reminders\n•  Feedback and community"
        })
      : t("panel_offer.highlights_placeholder", {
          defaultValue: "What's included…"
        })

  return (
    <CoverPagePanelRichTextField<CoverPagePanelOfferValues>
      name="highlights"
      label={t("panel_offer.highlights", { defaultValue: "Highlights" })}
      placeholder={placeholder}
      disabled={disabled}
    />
  )
}

function OfferPresentationFields({
  disabled,
  kind,
  offer
}: {
  disabled: boolean
  kind: OfferKind
  offer: CoverPageOffer | null
}) {
  const { t } = useCoverPageTranslate()
  return (
    <PanelSection.Root>
      <PanelSection.Heading>
        {t("panel_offer.presentation", { defaultValue: "Presentation" })}
      </PanelSection.Heading>
      <PanelSection.Content gap="15px">
        <Field
          {...AUGMENTATION_PANEL_INPUT_PROPS}
          name="title"
          label={t("panel_offer.title", { defaultValue: "Title" })}
          placeholder={
            kind === OfferKind.CreateCohort
              ? t("panel_offer.private_cohort_title_examples", {
                  defaultValue: "Take it with your team, class, or group…"
                })
              : kind === OfferKind.JoinCohort
              ? t("panel_offer.join_cohort_title_examples", {
                  defaultValue: "Fall session, Self-paced, Study along…"
                })
              : t("panel_offer.offer_title", { defaultValue: "Offer title" })
          }
          disabled={disabled}
        />
        <OfferHighlightsField disabled={disabled} kind={kind} />
        <Field
          {...AUGMENTATION_PANEL_INPUT_PROPS}
          name="buttonLabel"
          label={t("panel_offer.button_label", {
            defaultValue: "Button Label"
          })}
          placeholder={getDefaultButtonLabel(kind, t)}
          disabled={disabled}
        />
      </PanelSection.Content>
    </PanelSection.Root>
  )
}

function OfferAccessFields({
  offer,
  kind
}: {
  offer: CoverPageOffer | null
  kind: OfferKind
}) {
  const { t } = useCoverPageTranslate()
  const form = useFormikContext<CoverPagePanelOfferValues>()
  const resourceId = useCoverPageContext((value) => value.resourceId)
  const isJoin = kind === OfferKind.JoinCohort
  // Only a Join offer takes its access and pricing from a Cohort. A Cohort
  // picked before switching type must not stand in for Private Cohort settings.
  const selectedCohortId = isJoin
    ? Number(form.values.targetCohortId || offer?.cohort_id)
    : 0
  // NOTE: maybe we should just add cohort to the offer schema and include it in our queries?
  const cohortQuery = useGroupQuery({
    variables: {
      id: selectedCohortId
    },
    skip: !selectedCohortId
  })
  const selectedCohort = isJoin ? cohortQuery.data?.group : undefined

  const accessLabel = selectedCohort
    ? getCohortAccessLabel(selectedCohort, t)
    : offer
    ? getOfferAccessLabel(offer, t)
    : form.values.accessLabel || null
  const pricingLabel = selectedCohort
    ? getCohortPricingLabel(selectedCohort, t)
    : offer
    ? getOfferPricingLabel(offer)
    : form.values.pricingLabel || null
  const offerCohortId = getOfferCohortId(offer)
  const targetCohortId = selectedCohortId
  const groupSettingsUrl = offer
    ? `/manage/group/${resourceId}/${offerCohortId}/`
    : targetCohortId
    ? `/manage/group/${resourceId}/${targetCohortId}/`
    : `/manage/group/${resourceId}/new/`
  const commerceUrl = offer
    ? `/manage/group/${resourceId}/${offerCohortId}/commerce/`
    : targetCohortId
    ? `/manage/group/${resourceId}/${targetCohortId}/commerce/`
    : `/manage/group/${resourceId}/new/`
  const licensePricingUrl = `/manage/resource/${resourceId}/cohorts/license/pricing/`

  if (kind === OfferKind.External || kind === OfferKind.LicenseToSpace)
    return null
  // A Join offer's settings come from its Cohort, so wait for one to be picked.
  // A Private Cohort offer always shows where its settings live.
  if (isJoin && !accessLabel && !pricingLabel) return null

  return (
    <PanelSection.Root>
      <PanelSection.Heading>
        {t("panel_offer.access_pricing", {
          defaultValue: "Access & Pricing"
        })}
      </PanelSection.Heading>
      <PanelSection.Content gap={2}>
        {accessLabel ? (
          <OfferReadonlySetting
            icon="group"
            label={t("panel_offer.available_to", {
              defaultValue: "Available to"
            })}
            value={accessLabel}
            relativeTo={isJoin ? groupSettingsUrl : licensePricingUrl}
          />
        ) : null}
        {pricingLabel ? (
          <OfferReadonlySetting
            icon="credit-card"
            label={t("panel_offer.pricing", { defaultValue: "Pricing" })}
            value={pricingLabel}
            relativeTo={isJoin ? commerceUrl : licensePricingUrl}
          />
        ) : null}
        {!isJoin && !accessLabel && !pricingLabel ? (
          <Link relativeTo={licensePricingUrl}>
            <HStack
              as="span"
              gap={1}
              textStyle="augmentationPanel"
              textDecoration="none"
            >
              <Pathicon icon="gear" />
              <Text as="span" m={0}>
                {t("panel_offer.set_access_and_pricing", {
                  defaultValue: "Set access & pricing…"
                })}
              </Text>
            </HStack>
          </Link>
        ) : null}
      </PanelSection.Content>
    </PanelSection.Root>
  )
}

export function OfferAccessTargetField({
  disabled,
  kind
}: {
  disabled: boolean
  kind: OfferKind
}) {
  const { t } = useCoverPageTranslate()
  const openSubpanel = useOpenAugmentationSubpanel()
  const form = useFormikContext<CoverPagePanelOfferValues>()
  const resource = useCoverPageContext((value) => value.resource)
  const resourceId = useCoverPageContext((value) => value.resourceId)
  const offerableCohortsQuery = useCoverPageOfferableCohortsQuery({
    variables: {
      resourceId
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first"
  })
  const cohortOptions = useMemo(() => {
    return flattenEdges(offerableCohortsQuery.data?.groups) || []
  }, [offerableCohortsQuery.data?.groups])
  const isLoadingCohorts =
    offerableCohortsQuery.loading && !offerableCohortsQuery.data
  const sourcePathGroupId = resource?.curriculum_group?.id
  const sourcePathUrl =
    sourcePathGroupId && resource?.slug
      ? `/library/${resource.slug}/${sourcePathGroupId}/path/`
      : undefined

  if (kind === OfferKind.External) {
    return (
      <HStack align="center" gap={2} w="full">
        <Text {...AUGMENTATION_PANEL_STATIC_TEXT_PROPS}>
          {t("panel_offer.to", { defaultValue: "To" })}:
        </Text>
        <Box flex="1" minW="180px">
          <Field
            {...AUGMENTATION_PANEL_INPUT_PROPS}
            as={Input}
            name="externalUrl"
            hideLabel
            label={t("panel_offer.external_url", {
              defaultValue: "External URL"
            })}
            placeholder="https://..."
            disabled={disabled}
          />
        </Box>
      </HStack>
    )
  }

  if (kind === OfferKind.CreateCohort) {
    return (
      // Keep the source row available for when alternative sources can be selected.
      <HStack display="none" align="center" gap={2} wrap="wrap">
        <Text {...AUGMENTATION_PANEL_STATIC_TEXT_PROPS}>
          {t("header.from", { defaultValue: "From" })}:
        </Text>
        <OfferPillLink
          icon="path"
          value={t("path_preview.source_path", { defaultValue: "Source Path" })}
          to={sourcePathUrl}
          disabled={!sourcePathUrl}
        />
      </HStack>
    )
  }

  return (
    <HStack align="center" gap={2} wrap="wrap">
      <Text {...AUGMENTATION_PANEL_STATIC_TEXT_PROPS}>
        {t("panel_offer.cohort_label", { defaultValue: "Cohort:" })}
      </Text>
      <CustomSelect
        value={form.values.targetCohortId}
        disabled={disabled || isLoadingCohorts}
        placeholder={t("panel_offer.select_cohort", {
          defaultValue: "Select Cohort..."
        })}
        minW="180px"
        triggerProps={AUGMENTATION_PANEL_SELECT_PROPS}
        contentProps={{
          borderRadius: "augmentationPanel",
          minW: "max-content",
          maxW: "calc(100vw - 32px)"
        }}
        options={cohortOptions
          .map((cohort) => ({
            value: String(cohort.id),
            label:
              cohort.name ||
              t("panel_offer.cohort_with_number", {
                defaultValue: "Cohort {{number}}",
                number: cohort.id
              }),
            icon: getIconForCohort(cohort)
          }))
          .concat([
            {
              value: CREATE_COHORT_OPTION_VALUE,
              label: t("panel_offer.create_a_new_cohort_ellipsis", {
                defaultValue: "Create a new Cohort…"
              }),
              icon: "plus"
            }
          ])}
        onChange={(value) => {
          // The New Cohort panel returns here with the new Cohort selected.
          if (value === CREATE_COHORT_OPTION_VALUE) {
            openSubpanel(COVER_PAGE_NEW_OFFER_COHORT_PANEL_PATH)
            return
          }
          void form.setFieldValue("targetCohortId", value)
        }}
      />
    </HStack>
  )
}

function CoverPagePanelOfferForm({ offer }: { offer: CoverPageOffer | null }) {
  const { t } = useCoverPageTranslate()
  const getErrorMessage = useCoverPagePanelErrorMessage()
  const history = useHistory()
  const isEditable = useCoverPageIsEditable()
  const resourceId = useCoverPageContext((value) => value.resourceId)
  const refetchCoverPageOffers = useCoverPageContext(
    (value) => value.refetchCoverPageOffers
  )
  const scrollToSection = useCoverPageContext((value) => value.scrollToSection)
  const [createOffer] = useCreateOfferMutation()
  const [updateOffer] = useUpdateOfferMutation()

  const initialValues = useMemo(
    () => buildOfferInitialValues(offer, t),
    [offer, t]
  )

  const validate = useMemo(() => createOfferValidator(t), [t])

  async function handleSubmit(values: CoverPagePanelOfferValues) {
    try {
      if (!isEditable)
        throw new Error("You do not have permission to edit this Cover Page.")
      const kind = getCurrentOfferKind({
        offer,
        formKind: values.kind
      })
      const offerInput = buildCoverPageOfferInput(
        values,
        kind,
        t
      ) as StoreOfferInput

      if (offer) {
        await updateOffer({
          variables: {
            resourceId: resourceId,
            id: offer.id,
            offer: offerInput
          }
        })
      } else {
        await createOffer({
          variables: {
            resourceId: resourceId,
            offer: offerInput
          },
          // Remove the related cohort from the offerable cohorts query.
          update: (cache, { data }) => {
            cache.updateQuery<
              CoverPageOfferableCohortsQuery,
              CoverPageOfferableCohortsQueryVariables
            >(
              {
                query: CoverPageOfferableCohortsDocument,
                variables: { resourceId: resourceId }
              },
              (offerableCohortsData) => {
                return offerableCohortsData?.groups
                  ? {
                      ...offerableCohortsData,
                      groups: {
                        ...offerableCohortsData?.groups,
                        edges:
                          offerableCohortsData?.groups?.edges.filter(
                            (edge) =>
                              edge.node.id !== data?.createOffer?.cohort_id
                          ) || []
                      }
                    }
                  : null
              }
            )
          }
        })
      }

      await refetchCoverPageOffers()

      if (!offer) {
        history.push(`/${COVER_PAGE_PANEL_ROOT_KEY}`)
        navigateToCoverPageOffersSection(scrollToSection)
      }
    } catch (error) {
      toaster.create({
        title: getErrorMessage(
          error,
          offer
            ? t("panel_errors.update_offer", {
                defaultValue: "Error updating Cover Page's offer:"
              })
            : t("panel_errors.create_offer", {
                defaultValue: "Error creating Cover Page's offer:"
              })
        ),
        type: "error",
        duration: 4000,
        closable: true
      })
      throw error
    }
  }

  return (
    <FormikWithDiscardFormState
      formStateKey={
        offer
          ? ["cover-page", "offer", offer.id]
          : getNewOfferFormStateKey(resourceId)
      }
      initialValues={initialValues}
      validate={validate}
      validateOnMount
      enableReinitialize
      formStateOnSubmit={offer ? "preserve" : "clearOnSuccess"}
      onSubmit={handleSubmit}
    >
      {(form: FormikProps<CoverPagePanelOfferValues>) => {
        const disableFields = !isEditable || form.isSubmitting
        const kind = getCurrentOfferKind({
          offer,
          formKind: form.values.kind
        })

        const formId = "cover-page-panel-offer"

        return (
          <Form
            id={formId}
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%"
            }}
          >
            <KeyboardFormSubmit />
            <VStack align="stretch" gap="15px" w="full">
              {offer ? (
                <ExistingOfferAccessSummary offer={offer} kind={kind} />
              ) : (
                <OfferKindControl disabled={disableFields} kind={kind} />
              )}
              {kind === OfferKind.LicenseToSpace ? (
                <PanelSection.Group>
                  <OfferPresentationFields
                    disabled={disableFields}
                    kind={kind}
                    offer={offer}
                  />
                </PanelSection.Group>
              ) : (
                <CoverPagePanelOfferCreateCohortGate
                  gateEnabled={kind === OfferKind.CreateCohort}
                >
                  {offer ? null : (
                    <OfferAccessTargetField
                      disabled={disableFields}
                      kind={kind}
                    />
                  )}
                  <PanelSection.Group>
                    <OfferPresentationFields
                      disabled={disableFields}
                      kind={kind}
                      offer={offer}
                    />
                    <OfferAccessFields offer={offer} kind={kind} />
                  </PanelSection.Group>
                </CoverPagePanelOfferCreateCohortGate>
              )}
            </VStack>
            <CoverPagePanelFormActions
              form={form}
              formId={formId}
              disabled={!isEditable}
              submit={{
                children: offer
                  ? t("panel_offer.update_offer", {
                      defaultValue: "Update Offer"
                    })
                  : t("panel_offer.add_offer", { defaultValue: "Add Offer" })
              }}
              discard={{
                children: t("panel.discard", { defaultValue: "Discard" })
              }}
            />
          </Form>
        )
      }}
    </FormikWithDiscardFormState>
  )
}

export function buildCoverPageOfferInput(
  values: CoverPagePanelOfferValues,
  kind: OfferKind,
  t: CoverPageT
) {
  const offerInput: Record<string, unknown> = {
    title: values.title.trim(),
    description: values.highlights.trim(),
    cta_label: values.buttonLabel.trim() || null
  }

  if (kind === OfferKind.JoinCohort) {
    const cohortId = Number(values.targetCohortId)

    if (!cohortId) {
      throw new Error(
        t("panel_offer.select_a_cohort", { defaultValue: "Select a Cohort." })
      )
    }

    return {
      ...offerInput,
      kind: OfferKind.JoinCohort,
      cohort_id: cohortId
    }
  }

  if (kind === OfferKind.CreateCohort) {
    return {
      ...offerInput,
      kind: OfferKind.CreateCohort
    }
  }

  if (kind === OfferKind.LicenseToSpace) {
    return {
      ...offerInput,
      kind: OfferKind.LicenseToSpace
    }
  }

  const externalUrl = values.externalUrl.trim()

  if (!externalUrl) {
    throw new Error(
      t("panel_offer.enter_a_link", { defaultValue: "Enter a link." })
    )
  }

  return {
    ...offerInput,
    kind: OfferKind.External,
    external_url: externalUrl
  }
}

export function CoverPagePanelOfferCreateRoute() {
  return <CoverPagePanelOfferForm offer={null} />
}

export const CoverPagePanelOfferRoute: AugmentationComponent<{
  offerId: string
}> = ({ route }) => {
  const offer = useCoverPageOffer(route?.match?.params?.offerId || "")

  if (!offer) {
    return (
      <Text fontSize="sm" color="blackAlpha.700" m={0}>
        Offer not found.
      </Text>
    )
  }

  return <CoverPagePanelOfferForm offer={offer} />
}
